// Befehle, die nur ein bestimmter Bildschirm kennt und die die Befehlspalette
// des Rahmens trotzdem anbietet ("Notiz zu <Name>").
//
// Ein Bildschirm meldet den Befehl an, solange er eingehaengt ist; der Rahmen
// liest ihn. Der Rahmen kennt so keinen Bildschirm, und kein Bildschirm kennt
// die Palette. Der Kontext liegt hier und nicht in layout.tsx, damit ein
// Bildschirm den Hook ohne Rueckimport des Rahmens nutzen kann.

import { createContext, useContext, useEffect, useRef } from 'react';

export type KontextbefehlId = 'notiz';

export interface Kontextbefehl {
  readonly label: string;
  readonly ausfuehren: () => void;
}

export type KontextbefehleStand = Readonly<Partial<Record<KontextbefehlId, Kontextbefehl>>>;

export const KontextbefehleKontext = createContext<(id: KontextbefehlId, befehl: Kontextbefehl | undefined) => void>(
  () => undefined,
);

/**
 * Meldet den Befehl an, solange `befehl` gesetzt ist. `ausfuehren` darf bei
 * jedem Render ein neues Objekt sein: gemeldet wird nur bei Wechsel des Labels,
 * ausgefuehrt wird immer die juengste Fassung.
 */
export function useKontextbefehl(id: KontextbefehlId, befehl: Kontextbefehl | undefined): void {
  const melden = useContext(KontextbefehleKontext);
  const juengster = useRef(befehl);
  juengster.current = befehl;
  const label = befehl?.label;
  useEffect(() => {
    if (label === undefined) {
      return undefined;
    }
    melden(id, { label, ausfuehren: () => juengster.current?.ausfuehren() });
    return () => melden(id, undefined);
  }, [melden, id, label]);
}

/**
 * Oeffnet den Schnellzugriff, wo der Rahmen ihn anbietet. Ohne Anbieter (Rahmen
 * fehlt oder die Palette ist nicht verfuegbar) ist der Wert `undefined`; der
 * Aufrufer bietet dann einen Link auf die Kontaktsuche an.
 */
export const SchnellzugriffKontext = createContext<(() => void) | undefined>(undefined);

export function useSchnellzugriff(): (() => void) | undefined {
  return useContext(SchnellzugriffKontext);
}
